import { getGrid } from '../../grid';
import type { EventHub } from '../../events/EventHub';
import { SKILL_TARGETING_STATE_EVENT, type SkillTargetingState } from '../../events/SkillTargetingEvents';
import type { GameState, MapDefinition, TileData, TilePosition } from '../../model/gamestate/GameState';
import type { ActiveZoneData } from '../../spacetime/modules/SkillModule';
import type {
  ActorHoverChangedDetail,
  ActorTargetDetail,
  HoveredActorInfo,
  InterpolatedActorData,
  PixelPosition,
  TileHoverChangedDetail,
  TileMapRendererOptions,
  TileSelectedDetail,
  VisualEffect,
} from '../tilemap/types';
import { EntityManager } from '../tilemap/EntityManager';
import { EntityContextMenuController } from '../tilemap/ui/EntityContextMenuController';
import { DEFAULT_FRAME_DELTA_MS, MAX_FRAME_DELTA_MS } from '../tilemap/constants';
import { World2DRenderer } from './World2DRenderer';
import type { DynamicSceneRenderData } from './contracts';
import type { DynamicHitTarget } from './layers/DynamicEntityLayer';
import type { PathTileAtlasId } from '../../terrain/pathTileRegistry';
import type { BiomeType } from '../../model/gamestate/types';
import { resolveTileDrawMetrics } from '../tilemap/board';

type MapObjectPlacementPreviewDetail =
  | {
      active: true;
      assetId: string;
      previewMode: 'tile-anchor' | 'map-anchor';
      tileX?: number;
      tileY?: number;
      mapX?: number;
      mapY?: number;
      offsetX?: number;
      offsetY?: number;
      sizeMultiplier: number;
      flipHorizontal: boolean;
      flipVertical: boolean;
      rotationSteps: number;
    }
  | {
      active: false;
      assetId: null;
      previewMode: 'tile-anchor';
      sizeMultiplier: number;
      flipHorizontal: boolean;
      flipVertical: boolean;
      rotationSteps: number;
    };

type PathTilePlacementPreviewDetail =
  | {
      active: true;
      atlasId: PathTileAtlasId;
      tileIndex: number;
      autotile: boolean;
      flipHorizontal: boolean;
      flipVertical: boolean;
      rotationSteps: number;
    }
  | {
      active: false;
      atlasId: null;
      tileIndex: null;
      autotile: false;
    };

type BiomeRegionPlacementPreviewDetail =
  | {
      active: true;
      biome: BiomeType;
      startX: number;
      startY: number;
      endX: number;
      endY: number;
    }
  | {
      active: false;
      biome: null;
    };

export class TileMapRendererWorld2D extends EventTarget {
  private static readonly WHEEL_ZOOM_SENSITIVITY = 0.0015;
  private static readonly INTERPOLATION_SPEED_SCALE = 0.25;

  public static async create(options: TileMapRendererOptions): Promise<TileMapRendererWorld2D> {
    const renderer = new TileMapRendererWorld2D(options);
    await renderer.initialize();
    return renderer;
  }

  private readonly container: HTMLElement;
  private readonly gameState: GameState;
  private readonly eventHub: EventHub | null;
  private readonly basePixelsPerWorldUnit: number;
  private readonly entityManager = new EntityManager();
  private worldRenderer: World2DRenderer | null = null;
  private entityContextMenuController: EntityContextMenuController | null = null;
  private currentMap: MapDefinition;
  private currentMapId: string;
  private cameraFocus: TilePosition | null = null;
  private cameraFollowingSelf = true;
  private lastCharacterPosition: TilePosition | null = null;
  private localCharacterRotation = 180;
  private playerDisplayName = '';
  private queuePreview: (TilePosition | null)[] = [];
  private activeZones: ActiveZoneData[] = [];
  private portalTransitioning = false;
  private hoveredTileKey: string | null = null;
  private hoveredActorId: string | null = null;
  private skillTargetingState: SkillTargetingState | null = null;
  private animationFramePending = false;
  private lastAnimationTimestamp: number | null = null;
  private lastEntityProjectionPixelsPerWorldUnit: number | null = null;

  private constructor(options: TileMapRendererOptions) {
    super();
    this.container = options.container;
    this.gameState = options.gameState;
    this.eventHub = options.eventHub ?? null;
    this.currentMap = options.map;
    this.currentMapId = options.gameState.getCurrentMapId();
    this.basePixelsPerWorldUnit = options.tileSize ?? 73;
  }

  private async initialize(): Promise<void> {
    this.worldRenderer = await World2DRenderer.create({
      container: this.container,
      map: this.currentMap,
      basePixelsPerWorldUnit: this.basePixelsPerWorldUnit,
    });
    this.worldRenderer.setZoomCommitListener(() => {
      this.lastEntityProjectionPixelsPerWorldUnit = null;
      this.syncDynamicScene(0);
      if (this.cameraFollowingSelf) {
        const selfState = this.entityManager.getSelfActorState(this.gameState);
        if (selfState) {
          this.worldRenderer?.setCameraWorldCenter({ x: selfState.currentCenter.x, y: selfState.currentCenter.y });
        }
      }
    });
    this.worldRenderer.setActiveRadius(this.activeRadius);
    this.worldRenderer.setSkillTargetingState(this.skillTargetingState);
    if (this.worldRenderer.view instanceof HTMLCanvasElement) {
      this.entityContextMenuController = new EntityContextMenuController(this.container, this.worldRenderer.view, this);
      this.entityContextMenuController.registerListeners();
    }
    this.bindPointerEvents();
    this.bindSharedEvents();
    this.bindPreviewEvents();
  }

  public get view(): HTMLElement | null {
    return this.worldRenderer?.view ?? null;
  }

  public get activeRadius(): number {
    return this.gameState.getViewRadius();
  }

  public getCameraFocus(): TilePosition | null {
    return this.cameraFocus;
  }

  public getCurrentMap(): MapDefinition {
    return this.currentMap;
  }

  public getCurrentGrid() {
    return getGrid(this.currentMap.gridType);
  }

  public getViewSize(): { width: number; height: number } {
    return this.worldRenderer?.getViewSize() ?? { width: 0, height: 0 };
  }

  public worldToScreenCenter(x: number, y: number): PixelPosition {
    const screen = this.requireWorldRenderer().getCamera().tileCenterToScreen(x, y, this.getCurrentGrid());
    return { x: screen.x, y: screen.y };
  }

  public worldCenterToScreen(center: PixelPosition): PixelPosition {
    const screen = this.requireWorldRenderer().getCamera().worldPointToScreen(center, this.getCurrentGrid());
    return { x: screen.x, y: screen.y };
  }

  public mapPositionToScreen(mapX: number, mapY: number): PixelPosition | null {
    const screen = this.requireWorldRenderer().getCamera().tileOriginToScreen(mapX, mapY, this.getCurrentGrid());
    return { x: screen.x, y: screen.y };
  }

  public screenToTile(screenX: number, screenY: number): TilePosition | null {
    return this.requireWorldRenderer().getCamera().screenToTile(
      { x: screenX, y: screenY },
      this.getCurrentGrid(),
      this.currentMap.width,
      this.currentMap.height,
    ) as TilePosition | null;
  }

  public screenToMapPosition(event: MouseEvent | DragEvent): PixelPosition | null {
    const point = this.getLocalPointerPosition(event);
    if (!point) {
      return null;
    }
    const tile = this.screenToTile(point.x, point.y);
    if (!tile) {
      return null;
    }
    return { x: tile.x, y: tile.y };
  }

  public setCameraFocus(position: TilePosition): void {
    this.cameraFocus = { ...position };
    this.cameraFollowingSelf = false;
    this.requireWorldRenderer().setCameraCenter({ x: position.x, y: position.y });
  }

  public panCameraByScreen(deltaX: number, deltaY: number): void {
    const renderer = this.requireWorldRenderer();
    this.cameraFollowingSelf = false;
    renderer.panByScreenDelta(-deltaX, -deltaY);
    const viewport = renderer.getCamera().getViewportSize();
    const centerTile = renderer.getCamera().screenToTile(
      { x: viewport.width / 2, y: viewport.height / 2 },
      this.getCurrentGrid(),
      this.currentMap.width,
      this.currentMap.height,
    );
    this.cameraFocus = centerTile ? { x: centerTile.x, y: centerTile.y } : this.cameraFocus;
  }

  public rotateCameraOrbit(_direction: 'left' | 'right'): void {
  }

  public getVisualCharacterPosition(): TilePosition | null {
    return this.lastCharacterPosition;
  }

  public markActorSnap(_entityId: string, _target: TilePosition): void {
    this.entityManager.markActorSnap(_entityId, _target);
  }

  public setCharacterPosition(position: TilePosition, options?: { immediate?: boolean; rotation?: number }): void {
    this.lastCharacterPosition = { ...position };
    this.localCharacterRotation = options?.rotation ?? this.localCharacterRotation;
    this.syncDynamicScene();

    if (options?.immediate || this.cameraFollowingSelf || !this.cameraFocus) {
      this.cameraFollowingSelf = true;
      this.cameraFocus = { ...position };
      if (options?.immediate) {
        this.requireWorldRenderer().setCameraCenter({ x: position.x, y: position.y });
      }
    } else {
      this.requestRender();
    }
  }

  public setQueuePreview(queue: (TilePosition | null)[]): void {
    this.queuePreview = [...queue];
    this.requireWorldRenderer().setQueuePreview(this.queuePreview);
  }

  public setActiveZones(zones: ActiveZoneData[]): void {
    this.activeZones = [...zones];
    this.requireWorldRenderer().setActiveZones(this.activeZones);
  }

  public requestRender(): void {
    this.syncDynamicScene();
    this.requireWorldRenderer().requestRender();
  }

  public setPlayerDisplayName(displayName: string): void {
    this.playerDisplayName = displayName;
  }

  public setSkillTargetingActive(active: boolean): void {
    if (!active) {
      this.skillTargetingState = {
        active: false,
        skillId: null,
        requiredSlots: 0,
        slotTypes: [],
        hovered: null,
        selected: [],
      };
      this.requireWorldRenderer().setSkillTargetingState(this.skillTargetingState);
    }
    this.requireWorldRenderer().setSkillTargetingActive(active);
  }

  public addEffect(_effect: VisualEffect): void {
  }

  public isPortalTransitioning(): boolean {
    return this.portalTransitioning;
  }

  public startPortalTransition(onFadeOutComplete: () => void | Promise<void>, onComplete: () => void): void {
    this.portalTransitioning = true;
    Promise.resolve(onFadeOutComplete())
      .catch(console.error)
      .finally(() => {
        onComplete();
        this.portalTransitioning = false;
        if (!this.worldRenderer) {
          return;
        }
        this.requestRender();
      });
  }

  public cancelPortalTransition(): void {
    this.portalTransitioning = false;
    this.requestRender();
  }

  public playAnimation(_animationName: string, _payloadJson: string): void {
  }

  public startPortalFadeIn(): void {
    this.portalTransitioning = false;
    this.requestRender();
  }

  public swapToNewMap(map: MapDefinition, mapId: string): void {
    this.currentMap = map;
    this.currentMapId = mapId;
    this.cameraFocus = null;
    this.cameraFollowingSelf = true;
    this.entityManager.clear();
    this.lastAnimationTimestamp = null;
    this.lastEntityProjectionPixelsPerWorldUnit = null;
    this.requireWorldRenderer().setMap(map);
    this.syncDynamicScene();
  }

  public refreshCurrentMap(map: MapDefinition): void {
    this.currentMap = map;
    this.requireWorldRenderer().setMap(map);
    this.syncDynamicScene();
  }

  public destroy(): void {
    this.unbindPointerEvents();
    this.unbindSharedEvents();
    this.unbindPreviewEvents();
    this.entityContextMenuController?.destroy();
    this.entityContextMenuController = null;
    this.worldRenderer?.destroy();
    this.worldRenderer = null;
  }

  private bindPointerEvents(): void {
    const view = this.view;
    if (!(view instanceof HTMLCanvasElement)) {
      return;
    }
    view.addEventListener('wheel', this.handleWheel, { passive: false });
    view.addEventListener('pointermove', this.handlePointerMove);
    view.addEventListener('pointerleave', this.handlePointerLeave);
    view.addEventListener('pointerdown', this.handlePointerDown);
    view.addEventListener('contextmenu', this.handleContextMenu);
  }

  private unbindPointerEvents(): void {
    const view = this.view;
    if (!(view instanceof HTMLCanvasElement)) {
      return;
    }
    view.removeEventListener('wheel', this.handleWheel);
    view.removeEventListener('pointermove', this.handlePointerMove);
    view.removeEventListener('pointerleave', this.handlePointerLeave);
    view.removeEventListener('pointerdown', this.handlePointerDown);
    view.removeEventListener('contextmenu', this.handleContextMenu);
  }

  private bindSharedEvents(): void {
    this.eventHub?.on<SkillTargetingState>(SKILL_TARGETING_STATE_EVENT, this.handleSkillTargetingState);
  }

  private unbindSharedEvents(): void {
    this.eventHub?.off<SkillTargetingState>(SKILL_TARGETING_STATE_EVENT, this.handleSkillTargetingState);
  }

  private bindPreviewEvents(): void {
    this.addEventListener('mapObjectPlacementPreviewChanged', this.handleMapObjectPlacementPreviewChanged as EventListener);
    this.addEventListener('pathTilePlacementPreviewChanged', this.handlePathTilePlacementPreviewChanged as EventListener);
    this.addEventListener('biomeRegionPlacementPreviewChanged', this.handleBiomeRegionPlacementPreviewChanged as EventListener);
  }

  private unbindPreviewEvents(): void {
    this.removeEventListener('mapObjectPlacementPreviewChanged', this.handleMapObjectPlacementPreviewChanged as EventListener);
    this.removeEventListener('pathTilePlacementPreviewChanged', this.handlePathTilePlacementPreviewChanged as EventListener);
    this.removeEventListener('biomeRegionPlacementPreviewChanged', this.handleBiomeRegionPlacementPreviewChanged as EventListener);
  }

  private readonly handleWheel = (event: WheelEvent): void => {
    if (event.ctrlKey) {
      return;
    }
    event.preventDefault();
    const renderer = this.requireWorldRenderer();
    const nextZoom = renderer.getEffectiveZoomFactor() * Math.exp(-event.deltaY * TileMapRendererWorld2D.WHEEL_ZOOM_SENSITIVITY);
    if (this.cameraFollowingSelf) {
      renderer.setZoomFactor(nextZoom);
      return;
    }
    const point = this.getLocalPointerPosition(event);
    if (!point) {
      return;
    }
    renderer.zoomAboutScreenPoint(nextZoom, point, this.getCurrentGrid(), this.currentMap.width, this.currentMap.height);
  };

  private readonly handlePointerMove = (event: PointerEvent): void => {
    const point = this.getLocalPointerPosition(event);
    if (!point) {
      return;
    }
    const worldRenderer = this.requireWorldRenderer();
    const tile = this.screenToTile(point.x, point.y);
    const tileKey = tile ? `${tile.x},${tile.y}` : null;
    const actor = worldRenderer.hitTestDynamicEntity(point.x, point.y);
    this.updateHoveredActor(actor);
    worldRenderer.setHoveredActor(actor, actor ? point : null);
    worldRenderer.setHoveredTile(tile, tile ? this.isTileInFog(tile) : false);
    if (tileKey !== this.hoveredTileKey) {
      this.hoveredTileKey = tileKey;
      const detail: TileHoverChangedDetail = {
        tile,
        inFog: tile ? this.isTileInFog(tile) : false,
      };
      this.dispatchEvent(new CustomEvent<TileHoverChangedDetail>('tileHoverChanged', { detail }));
    }

  };

  private readonly handlePointerLeave = (): void => {
    this.hoveredTileKey = null;
    const worldRenderer = this.requireWorldRenderer();
    worldRenderer.setHoveredTile(null, false);
    worldRenderer.setHoveredActor(null, null);
    this.dispatchEvent(new CustomEvent<TileHoverChangedDetail>('tileHoverChanged', {
      detail: { tile: null, inFog: false },
    }));
    this.updateHoveredActor(null);
  };

  private readonly handlePointerDown = (event: PointerEvent): void => {
    const point = this.getLocalPointerPosition(event);
    if (!point) {
      return;
    }
    const actor = this.requireWorldRenderer().hitTestDynamicEntity(point.x, point.y);
    if (actor) {
      const actorSelectedEvent = new CustomEvent<ActorTargetDetail>('actorSelected', {
        detail: this.toActorTargetDetail(actor),
        cancelable: true,
      });
      this.dispatchEvent(actorSelectedEvent);
      if (actorSelectedEvent.defaultPrevented) {
        return;
      }
    }
    const tilePosition = this.screenToTile(point.x, point.y);
    if (!tilePosition) {
      return;
    }
    const tile = this.currentMap.tiles[tilePosition.y]?.[tilePosition.x] ?? null;
    if (!tile) {
      return;
    }
    const detail: TileSelectedDetail = {
      tile: tile as TileData,
      tilePosition,
      mapPosition: { x: tilePosition.x, y: tilePosition.y },
      screenPosition: point,
      shiftKey: event.shiftKey,
      button: event.button,
      buttons: event.buttons,
    };
    this.dispatchEvent(new CustomEvent<TileSelectedDetail>('tileSelected', { detail }));
  };

  private readonly handleContextMenu = (event: MouseEvent): void => {
    event.preventDefault();
    const point = this.getLocalPointerPosition(event);
    if (!point) {
      return;
    }
    const actor = this.requireWorldRenderer().hitTestDynamicEntity(point.x, point.y);
    const contextActor = actor ? this.toContextActor(actor) : null;
    if (!contextActor || !this.entityContextMenuController) {
      this.entityContextMenuController?.hideEntityContextMenu();
      return;
    }
    this.entityContextMenuController.showEntityContextMenu(point, contextActor);
  };

  private getLocalPointerPosition(event: MouseEvent | PointerEvent | WheelEvent | DragEvent): PixelPosition | null {
    const rect = this.container.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return null;
    }
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  private readonly handleSkillTargetingState = (state: SkillTargetingState | undefined): void => {
    this.skillTargetingState = state ?? {
      active: false,
      skillId: null,
      requiredSlots: 0,
      slotTypes: [],
      hovered: null,
      selected: [],
    };
    const worldRenderer = this.worldRenderer;
    if (!worldRenderer) {
      return;
    }
    worldRenderer.setSkillTargetingActive(this.skillTargetingState.active);
    worldRenderer.setSkillTargetingState(this.skillTargetingState);
  };

  private readonly handleMapObjectPlacementPreviewChanged = (event: Event): void => {
    const detail = (event as CustomEvent<MapObjectPlacementPreviewDetail>).detail;
    this.requireWorldRenderer().setMapObjectPlacementPreview({
      active: Boolean(detail?.active),
      assetId: detail?.assetId ?? null,
      previewMode: detail?.previewMode === 'map-anchor' ? 'map-anchor' : 'tile-anchor',
      tileX: typeof detail?.tileX === 'number' ? detail.tileX : undefined,
      tileY: typeof detail?.tileY === 'number' ? detail.tileY : undefined,
      mapX: typeof detail?.mapX === 'number' ? detail.mapX : undefined,
      mapY: typeof detail?.mapY === 'number' ? detail.mapY : undefined,
      offsetX: typeof detail?.offsetX === 'number' ? detail.offsetX : undefined,
      offsetY: typeof detail?.offsetY === 'number' ? detail.offsetY : undefined,
      sizeMultiplier: typeof detail?.sizeMultiplier === 'number' && detail.sizeMultiplier > 0 ? detail.sizeMultiplier : 1,
      flipHorizontal: Boolean(detail?.flipHorizontal),
      flipVertical: Boolean(detail?.flipVertical),
      rotationSteps: typeof detail?.rotationSteps === 'number' ? detail.rotationSteps : 0,
    });
  };

  private readonly handlePathTilePlacementPreviewChanged = (event: Event): void => {
    const detail = (event as CustomEvent<PathTilePlacementPreviewDetail>).detail;
    this.requireWorldRenderer().setPathTilePlacementPreview({
      active: Boolean(detail?.active),
      atlasId: detail?.atlasId ?? null,
      tileIndex: typeof detail?.tileIndex === 'number' ? detail.tileIndex : null,
      autotile: detail?.autotile === true,
      flipHorizontal: Boolean(detail?.flipHorizontal),
      flipVertical: Boolean(detail?.flipVertical),
      rotationSteps: typeof detail?.rotationSteps === 'number' ? detail.rotationSteps : 0,
    });
  };

  private readonly handleBiomeRegionPlacementPreviewChanged = (event: Event): void => {
    const detail = (event as CustomEvent<BiomeRegionPlacementPreviewDetail>).detail;
    this.requireWorldRenderer().setBiomeRegionPlacementPreview({
      active: Boolean(detail?.active),
      biome: detail?.biome ?? null,
      startX: typeof detail?.startX === 'number' ? detail.startX : undefined,
      startY: typeof detail?.startY === 'number' ? detail.startY : undefined,
      endX: typeof detail?.endX === 'number' ? detail.endX : undefined,
      endY: typeof detail?.endY === 'number' ? detail.endY : undefined,
    });
  };

  private isTileInFog(tile: TilePosition): boolean {
    if (this.currentMap.visibilityMode === 'full_map') {
      return false;
    }
    if (!this.lastCharacterPosition) {
      return false;
    }
    return this.getCurrentGrid().distance(
      this.lastCharacterPosition.x,
      this.lastCharacterPosition.y,
      tile.x,
      tile.y,
    ) > this.activeRadius;
  }

  private syncDynamicScene(deltaSeconds = DEFAULT_FRAME_DELTA_MS / 1000): void {
    const renderer = this.requireWorldRenderer();
    const currentPixelsPerWorldUnit = renderer.getCamera().getPixelsPerWorldUnit();
    if (
      this.lastEntityProjectionPixelsPerWorldUnit === null
      || Math.abs(this.lastEntityProjectionPixelsPerWorldUnit - currentPixelsPerWorldUnit) > 0.0001
    ) {
      this.entityManager.reprojectActorWorldCenters((position) => this.resolveWorldCenter(position));
      this.lastEntityProjectionPixelsPerWorldUnit = currentPixelsPerWorldUnit;
    }
    const { hasActiveAnimation } = this.entityManager.updateActorVisuals(
      deltaSeconds * TileMapRendererWorld2D.INTERPOLATION_SPEED_SCALE,
      this.gameState,
      this.lastCharacterPosition,
      null,
      this.localCharacterRotation,
      this.playerDisplayName,
      (position) => this.resolveWorldCenter(position),
    );
    renderer.setActiveRadius(this.activeRadius);
    const selfState = this.entityManager.getSelfActorState(this.gameState);
    renderer.setSelfPosition(selfState ? { x: selfState.current.x, y: selfState.current.y } : this.lastCharacterPosition);
    renderer.setQueuePreview(this.queuePreview);
    renderer.setActiveZones(this.activeZones);
    renderer.setDynamicScene(this.buildDynamicScene());
    if (this.cameraFollowingSelf && selfState && !renderer.isZoomTransitionActive()) {
      renderer.setCameraWorldCenter({ x: selfState.currentCenter.x, y: selfState.currentCenter.y });
    }
    if (hasActiveAnimation) {
      this.scheduleAnimationFrame();
    }
  }

  private buildDynamicScene(): DynamicSceneRenderData {
    const actors = [];
    for (const state of this.entityManager.getActorVisuals().values()) {
      const actor = state.data as InterpolatedActorData;
      actors.push({
        id: actor.id,
        playerId: actor.playerId,
        kind: actor.kind,
        entityType: actor.entityType,
        displayName: actor.displayName,
        guildName: undefined,
        accessoriesJson: actor.accessoriesJson,
        x: state.current.x,
        y: state.current.y,
        worldCenterX: state.currentCenter.x,
        worldCenterY: state.currentCenter.y,
        rotation: actor.rotation ?? 180,
        color: actor.color || (actor.kind === 'mob' ? '#ef4444' : actor.kind === 'self' ? '#3b82f6' : '#f59e0b'),
        alpha: state.opacity,
        isMoving: Math.abs(state.current.x - state.target.x) > 0.01 || Math.abs(state.current.y - state.target.y) > 0.01,
        hp: actor.hp,
        maxHp: actor.maxHp,
        stamina: actor.stamina,
        maxStamina: actor.maxStamina,
        mobInventoryValue: actor.mobInventoryValue ?? null,
        mobInventoryItemsJson: actor.mobInventoryItemsJson ?? null,
      });
    }

    const markers = [];
    for (const npc of this.gameState.getNpcRuntime()) {
      markers.push({
        id: npc.instanceId,
        kind: npc.npcType === 'chest' ? 'chest' as const : 'npc' as const,
        x: npc.x,
        y: npc.y,
        label: npc.npcType,
      });
    }

    return { actors, markers };
  }

  private scheduleAnimationFrame(): void {
    if (this.animationFramePending) {
      return;
    }
    this.animationFramePending = true;
    requestAnimationFrame((timestamp) => {
      this.animationFramePending = false;
      if (!this.worldRenderer) {
        return;
      }
      const previousTimestamp = this.lastAnimationTimestamp;
      this.lastAnimationTimestamp = timestamp;
      const deltaMs = previousTimestamp === null
        ? DEFAULT_FRAME_DELTA_MS
        : Math.min(Math.max(timestamp - previousTimestamp, 0), MAX_FRAME_DELTA_MS);
      this.syncDynamicScene(deltaMs / 1000);
      this.worldRenderer.requestRender();
    });
  }

  private resolveWorldCenter(position: TilePosition): PixelPosition {
    const grid = this.getCurrentGrid();
    const pixelsPerWorldUnit = this.worldRenderer?.getCamera().getPixelsPerWorldUnit() ?? this.basePixelsPerWorldUnit;
    const tileMetrics = resolveTileDrawMetrics(grid, pixelsPerWorldUnit, position.x, position.y);
    const q0 = Math.floor(position.x);
    const r0 = Math.floor(position.y);
    const fx = position.x - q0;
    const fy = position.y - r0;
    const c00 = grid.tileCentroid(pixelsPerWorldUnit, q0, r0);
    const c10 = grid.tileCentroid(pixelsPerWorldUnit, q0 + 1, r0);
    const c01 = grid.tileCentroid(pixelsPerWorldUnit, q0, r0 + 1);
    const c11 = grid.tileCentroid(pixelsPerWorldUnit, q0 + 1, r0 + 1);
    const centroidX =
      c00.x * (1 - fx) * (1 - fy)
      + c10.x * fx * (1 - fy)
      + c01.x * (1 - fx) * fy
      + c11.x * fx * fy;
    const centroidY =
      c00.y * (1 - fx) * (1 - fy)
      + c10.y * fx * (1 - fy)
      + c01.y * (1 - fx) * fy
      + c11.y * fx * fy;
    return {
      x: tileMetrics.originX + centroidX,
      y: tileMetrics.originY + centroidY,
    };
  }

  private updateHoveredActor(actor: DynamicHitTarget | null): void {
    const nextId = actor?.id ?? null;
    if (nextId === this.hoveredActorId) {
      return;
    }
    this.hoveredActorId = nextId;
    this.dispatchEvent(new CustomEvent<ActorHoverChangedDetail>('actorHoverChanged', {
      detail: {
        actor: actor ? this.toActorTargetDetail(actor) : null,
      },
    }));
  }

  private toActorTargetDetail(actor: DynamicHitTarget): ActorTargetDetail {
    return {
      actorId: actor.id,
      kind: actor.kind,
      displayName: actor.displayName?.trim() || actor.id,
      position: { x: actor.x, y: actor.y },
    };
  }

  private toContextActor(actor: DynamicHitTarget): HoveredActorInfo | null {
    if (actor.kind === 'self') {
      return null;
    }
    return {
      id: actor.id,
      playerId: actor.playerId,
      kind: actor.kind,
      entityType: actor.entityType,
      center: { x: actor.centerX, y: actor.centerY },
      position: { x: actor.x, y: actor.y },
      displayName: actor.displayName,
      tradeTargetName: actor.tradeTargetName,
      hp: actor.hp,
      maxHp: actor.maxHp,
      stamina: actor.stamina,
      maxStamina: actor.maxStamina,
      rotation: actor.rotation,
      mobInventoryValue: actor.mobInventoryValue ?? null,
      mobInventoryItemsJson: actor.mobInventoryItemsJson ?? null,
    };
  }

  private requireWorldRenderer(): World2DRenderer {
    if (!this.worldRenderer) {
      throw new Error('World2DRenderer is not initialized.');
    }
    return this.worldRenderer;
  }
}
